import { Button, Stack } from '@eleveo/components-react';
import { UserAddIcon } from '@eleveo/icons-react';
import { FormApi } from 'final-form';
import { WithT } from 'i18next';
import React, { useEffect, useState } from 'react';
import { FormProps } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';

import { QueueForm } from './QueueForm';
import { QueuesBulkAssignForm } from './QueuesBulkAssignForm';
import { ASSIGN_QUEUE_BUTTON_TAG, SAVE_QUEUE_BUTTON_TAG } from './constants';
import { createValuesFromQueue } from './queuesUtils';
import { useAuth } from '../../app/AuthorizationProvider';
import { SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { SidebarForm } from '../../components/LayoutNext/SidebarForm';
import { ModalConfirmationNewStyle } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  Agent,
  Queue,
  QueueChannelType,
  useGetQueuesTagsQuery,
  useGetSupervisedAgentsQuery,
} from '../../graphql/components';
import { AcdQueuesResult, SkillsAllResult, TimezonesAllResult } from '../../graphql/types';
import { SortOrder, sortAgentsBy } from '../../types';
import { customSort } from '../../utils/sort';

import type { Values } from './types';

type QueueSidebarProps = {
  acdQueuesResult: AcdQueuesResult;
  handleCancel: () => void;
  handleSubmit: FormProps<Values>['onSubmit'];
  queue: Queue | null;
  skillsResult: SkillsAllResult;
  timezonesResult: TimezonesAllResult;
} & WithT;

const initialQueue = {
  acdQueueIds: [],
  acdQueueIdsEditable: true,
  assignedIds: null,
  channelType: '' as QueueChannelType,
  channelTypeEditable: true,
  id: '',
  interval: NaN,
  name: '',
  nameEditable: true,
  percentage: NaN,
  serviceLevelEditable: true,
  skillId: null,
  skillIdEditable: true,
  tag: '',
  tagEditable: true,
  timezone: '',
  timezoneEditable: true,
  used: false,
};

const isEmptyFieldValue = (value: any) => value === '' || value === undefined || value === null;

export const QueuesSidebar: React.FC<QueueSidebarProps> = ({
  acdQueuesResult,
  skillsResult,
  timezonesResult,
  queue,
  t,
  handleSubmit,
  handleCancel,
}) => {
  const [initialValues, setInitialValues] = useState<Values>(initialQueue);
  const { toggleSidebarExpanded, isSidebarExpanded } = useSidebar();
  const { userId } = useAuth();

  const [activeSkill, setActiveSkill] = useState<Values['skillId']>();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [showSkillModal, setShowSkillModal] = useState(false);

  const { data: agentsResult, loading: agentsLoading } = useGetSupervisedAgentsQuery({
    skip: !!queue && typeof activeSkill === 'undefined',
    variables: {
      supervisorId: userId,
      filters: {
        skillIds: activeSkill,
      },
    },
  });

  const { data: assignedAgentsResult, loading: assignedAgentsLoading } = useGetSupervisedAgentsQuery({
    fetchPolicy: 'no-cache',
    skip: !queue,
    variables: {
      supervisorId: userId,
      filters: {
        queueIds: queue?.id,
      },
    },
  });

  const tagsResult = useGetQueuesTagsQuery();

  useEffect(() => {
    if (queue) {
      setActiveSkill(queue.skillId);
      setInitialValues(createValuesFromQueue(queue));
    } else {
      setInitialValues(initialQueue);
    }
  }, [queue]);

  useEffect(() => {
    if (!agentsLoading) {
      const agentsData = agentsResult?.supervisedAgents?.agents ?? [];

      const agentsDataSorted = customSort(agentsData, sortAgentsBy.name, SortOrder.ASCENDING);

      setAgents(agentsDataSorted);
    }
  }, [agentsResult?.supervisedAgents?.agents, agentsLoading]);

  useEffect(() => {
    if (!assignedAgentsLoading && !!assignedAgentsResult?.supervisedAgents?.agents) {
      const assignedAgentsData = assignedAgentsResult?.supervisedAgents?.agents ?? [];
      const ids = assignedAgentsData.map((agent) => agent.id);

      setInitialValues((values) => ({
        ...values,
        assignedIds: ids,
      }));
    }
  }, [assignedAgentsResult?.supervisedAgents, assignedAgentsLoading]);

  const checkAssignedAgents = async (assignedIds: Values['assignedIds'], skillId: Values['skillId']) => {
    if (!skillId) {
      setActiveSkill(skillId);
      return;
    }

    const assignedAgents = agents.filter((agent) => assignedIds?.includes(agent.id));
    const assignedAgentsWithoutSelectedSkill = assignedAgents.filter((agent) => !agent.skillIds?.includes(skillId));

    if (assignedAgentsWithoutSelectedSkill.length > 0) {
      setShowSkillModal(true);
    } else {
      setActiveSkill(skillId);
    }
  };

  const handleCancelSkillModal = (form: FormApi<Values, Partial<Values>>, previousSkillId: Values['skillId']) => () => {
    setShowSkillModal(false);
    form.change('skillId', previousSkillId); // Return skill dropdown to original value
  };

  const handleSubmitSkillModal =
    (
      form: FormApi<Values, Partial<Values>>,
      assignedIds: Values['assignedIds'],
      skillId: NonNullable<Values['skillId']>
    ) =>
    () => {
      setShowSkillModal(false);

      setActiveSkill(skillId); // Load agents based on new selected skill

      // Assign only agents having selected skill
      const assignedAgents = agents.filter((agent) => assignedIds?.includes(agent.id));
      const assignedAgentsWithSelectedSkill = assignedAgents.filter((agent) => agent.skillIds?.includes(skillId));
      const agentWithSkillIds = assignedAgentsWithSelectedSkill.map((agent) => agent.id);

      form.change('assignedIds', agentWithSkillIds);
    };

  return (
    <SidebarForm<Values>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onExtend={toggleSidebarExpanded}
      name="queue-form"
      onClose={handleCancel}
      title={queue ? t('queue.editQueue') : t('queue.add')}
    >
      {({ form, pristine, invalid, values }) => (
        <>
          <OnChange name="skillId">
            {(value) => {
              const skillState = form.getFieldState('skillId');
              const assignedState = form.getFieldState('assignedIds');

              if (value === activeSkill || (isEmptyFieldValue(value) && isEmptyFieldValue(activeSkill))) {
                return;
              }

              if (
                !skillState?.initial ||
                skillState?.initial !== value ||
                (skillState?.initial === value && assignedState?.initial !== assignedState?.value)
              ) {
                checkAssignedAgents(values.assignedIds, value);
              } else {
                setActiveSkill(value);
              }
            }}
          </OnChange>
          <SidebarPanel>
            <Stack direction="horizontal" className="w-full">
              <Button testId={SAVE_QUEUE_BUTTON_TAG} isDisabled={pristine || invalid} color="success" type="submit">
                {t('layout.action.save')}
              </Button>
              <Button
                aria-expanded={isSidebarExpanded}
                testId={ASSIGN_QUEUE_BUTTON_TAG}
                onClick={toggleSidebarExpanded}
                isSelected={isSidebarExpanded}
                className="mis-auto"
              >
                <UserAddIcon /> {t('person.assignPeople')}
              </Button>
            </Stack>
          </SidebarPanel>
          <SidebarContent>
            <SidebarPart>
              <QueueForm
                acdQueueIdsEditable={initialValues.acdQueueIdsEditable}
                acdQueuesResult={acdQueuesResult}
                channelTypeEditable={initialValues.channelTypeEditable}
                nameEditable={initialValues.nameEditable}
                serviceLevelEditable={initialValues.serviceLevelEditable}
                skillIdEditable={initialValues.skillIdEditable}
                skillsResult={skillsResult}
                tagEditable={initialValues.tagEditable}
                tagsResult={tagsResult}
                timezoneEditable={initialValues.timezoneEditable}
                timezonesResult={timezonesResult}
                t={t}
              />
            </SidebarPart>
            {isSidebarExpanded && (
              <SidebarPart>
                <QueuesBulkAssignForm
                  agentsWithSkill={agents}
                  assignedIds={values.assignedIds ?? []}
                  assignIds={(ids) => form.change('assignedIds', ids)}
                  // When editing queue, until assignedIds is resolved, component should be in a loading state
                  loading={agentsLoading || assignedAgentsLoading || (!!queue && !values.assignedIds)}
                  t={t}
                />
              </SidebarPart>
            )}
            <ModalConfirmationNewStyle
              isOpen={showSkillModal}
              acceptCaption={t('skill.confirm')}
              cancelCaption={t('layout.action.cancel')}
              title={t('layout.warning')}
              onAccept={handleSubmitSkillModal(form, values.assignedIds, values.skillId!)}
              onCancel={handleCancelSkillModal(form, activeSkill)}
            >
              <p>
                <strong>{t('queue.modal.skillChangeBody')}</strong>
              </p>
              <p>{t('queue.modal.skillChangeBody2')}</p>
            </ModalConfirmationNewStyle>
          </SidebarContent>
        </>
      )}
    </SidebarForm>
  );
};
